import axios from "axios";
import {
  SnowTypeEntry,
  AvalancheTypeEntry,
  ImageTypeEntry,
} from "../types/report";

//function cleanPayload(obj: any): any {
//  if (Array.isArray(obj)) {
//    // Rekursiv für Arrays
//    return obj
//      .map(cleanPayload)
//      .filter(
//        (v) => v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)
//      );
//  } else if (typeof obj === "object" && obj !== null) {
//    // Rekursiv für Objekte
//    const cleaned: any = {};
//    for (const [key, value] of Object.entries(obj)) {
//      const cleanedValue = cleanPayload(value);
//      if (
//        cleanedValue !== null &&
//        cleanedValue !== "" &&
//        !(Array.isArray(cleanedValue) && cleanedValue.length === 0)
//      ) {
//        cleaned[key] = cleanedValue;
//      }
//    }
//    return cleaned;
//  }
//  return obj;
//}

// Reverse mapping for snow types (display name -> API value)
const avaTypeReverseMap: Record<string, string> = {
  Neuschnee: "newsnow",
  Triebschnee: "windslab",
  Gleitschnee: "glidesnow",
  Altschnee: "weaklayer",
  Nassschnee: "wetsnow",
};

const mapAvaTypeReverse = (displayName: string): string => {
  return avaTypeReverseMap[displayName] ?? displayName.toLowerCase();
};

const avaSituationReverseMap: Record<string, string> = {
  "": "keiner",
  "Keine Angabe": "keiner",
  "Einfache Lawinensituation": "leicht",
  "Schwierige Lawinensituation": "schwierig",
  "Komplexe Lawinensituation": "kompliziert",
};

const avaSituationReverse = (displayName: string): string => {
  return avaSituationReverseMap[displayName] ?? displayName.toLowerCase();
};

const snowTypeReverseMap: Record<string, string> = {
  Pulverschnee: "pulverschnee",
  Firn: "firn",
  "Tragfähiger Harschdeckel": "harschdeckel",
  Bruchharsch: "bruchharsch",
  Faulschnee: "faulschnee",
  Vereist: "vereist",
  Sommerschnee: "sommerschnee",
  Noppenpulver: "noppenpulver",
  Windbeeinflusst: "windbeeinflusst",
  "Wechselhafter Schnee": "wechselhafterschnee",
  Nassschnee: "nassschnee",
  Aper: "aper",
};

const mapSnowTypeReverse = (displayName: string): string => {
  return snowTypeReverseMap[displayName] ?? displayName.toLowerCase();
};

// Transform aspects array back to expo string
function transformAspectsToExpo(aspects: string[]): string {
  if (!aspects || aspects.length === 0) return "";

  const sortedAspects = aspects.sort((a, b) => {
    const order = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    return order.indexOf(a) - order.indexOf(b);
  });

  // Check if all aspects are selected
  if (sortedAspects.length === 8) return "all";

  // Check for consecutive ranges
  const aspectOrder = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

  // Find patterns for 2-aspect ranges
  if (sortedAspects.length === 2) {
    const first = sortedAspects[0];
    const second = sortedAspects[1];
    const firstIndex = aspectOrder.indexOf(first);
    const secondIndex = aspectOrder.indexOf(second);

    if (
      secondIndex - firstIndex === 1 ||
      (firstIndex === 7 && secondIndex === 0)
    ) {
      return `${first}-${second}`;
    }
  }

  // Find patterns for 3-aspect ranges
  if (sortedAspects.length === 3) {
    const patterns = [
      ["N", "NE", "E"],
      ["NE", "E", "SE"],
      ["E", "SE", "S"],
      ["SE", "S", "SW"],
      ["S", "SW", "W"],
      ["SW", "W", "NW"],
      ["W", "NW", "N"],
      ["NW", "N", "NE"],
    ];
    for (const pattern of patterns) {
      if (JSON.stringify(sortedAspects) === JSON.stringify(pattern)) {
        return `${pattern[0]}-${pattern[2]}`;
      }
    }
  }

  // Find patterns for 5-aspect ranges
  if (sortedAspects.length === 5) {
    const patterns = [
      ["N", "NE", "E", "SE", "S"],
      ["NE", "E", "SE", "S", "SW"],
      ["E", "SE", "S", "SW", "W"],
      ["SE", "S", "SW", "W", "NW"],
      ["S", "SW", "W", "NW", "N"],
      ["SW", "W", "NW", "N", "NE"],
      ["W", "NW", "N", "NE", "E"],
      ["NW", "N", "NE", "E", "SE"],
    ];
    for (const pattern of patterns) {
      if (JSON.stringify(sortedAspects) === JSON.stringify(pattern)) {
        return `${pattern[0]}-${pattern[4]}`;
      }
    }
  }

  // If no pattern matches, return the first aspect
  return sortedAspects[0];
}

// Upload image and return the ID
async function uploadImage(file: File): Promise<number | null> {
  try {
    const formData = new FormData();
    formData.append("files", file);

    const token = localStorage.getItem("jwt");
    const response = await axios.post(
      "https://backend.schnee.report/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    return response.data[0]?.id || null;
  } catch (error) {
    console.error("Error uploading image:", error);
    return null;
  }
}

export interface SaveReportData {
  title: string;
  validFrom: string;
  validUntil: string;
  regions: string[];
  snowEntries: SnowTypeEntry[];
  avaEntries: AvalancheTypeEntry[];
  avalancheSituation: string;
  markdownText: string;
  imageEntries: ImageTypeEntry[];
}

export async function saveReport(
  data: SaveReportData,
  publish: boolean,
  reportId?: string
): Promise<{
  success: boolean;
  documentId?: string;
  error?: string;
  resp?: string;
  hadToUpload?: boolean;
}> {
  try {
    // Upload new images
    let hadToUpload = false;
    const imageComponents = await Promise.all(
      data.imageEntries.map(async (entry) => {
        let pictureId: number | null = null;

        // If there's a new file to upload
        if (entry.file) {
          pictureId = await uploadImage(entry.file);
          hadToUpload = true;
        } else {
          pictureId = entry.rawImage.picture;
        }
        return {
          caption: entry.description,
          picture: pictureId,
        };
      })
    );

    // Transform snow entries
    const snowComponents = data.snowEntries
      .filter((snow) => snow.type != null && snow.type !== "")
      .map((entry) => ({
        snow_type: mapSnowTypeReverse(entry.type),
        from_altitude: parseInt(entry.altitudeFrom) || null,
        to_altitude: parseInt(entry.altitudeTo) || null,
        draw_middle_layer: entry.drawMiddleLayer,
        expos: entry.aspects.map((aspect) => ({
          expo: transformAspectsToExpo([aspect]),
        })),
      }));

    // Transform avalanche entries
    const avalancheComponents = data.avaEntries
      .filter((ava) => ava.type != null && ava.type !== "")
      .map((entry) => ({
        problem: mapAvaTypeReverse(entry.type),
        from_altitude: parseInt(entry.altitudeFrom) || null,
        to_altitude: parseInt(entry.altitudeTo) || null,
        draw_middle_layer: entry.drawMiddleLayer,
        expos: entry.aspects.map((aspect) => ({
          expo: transformAspectsToExpo([aspect]),
        })),
      }));

    // Prepare the payload
    console.log(data);

    // Helper function to safely convert to ISO string
    const toISOStringOrNull = (dateValue: string): string | null => {
      if (!dateValue) return null;
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date.toISOString();
    };

    const payload: any = {
      title: data.title,
      valid_from: toISOStringOrNull(data.validFrom),
      valid_until: toISOStringOrNull(data.validUntil),
      content: data.markdownText,
      grade_of_manageability: avaSituationReverse(data.avalancheSituation),
      regions: data.regions,
      snow: snowComponents,
      avalanche: avalancheComponents,
      images: imageComponents.filter((img) => img.picture !== null),
    };

    // If publishing, set publishedAt
    if (publish) {
      payload.publishedAt = new Date().toISOString();
      payload.title_en = "";
      payload.content_en = "";
    }

    const token = localStorage.getItem("jwt");
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    let response;
    if (reportId) {
      // Update existing report
      if (publish) {
        response = await axios.post(
          `https://backend.schnee.report/content-manager/collection-types/api::report.report/${reportId}` +
            (publish ? "/actions/publish?" : ""),
          payload,
          { headers }
        );
      } else {
        response = await axios.put(
          `https://backend.schnee.report/content-manager/collection-types/api::report.report/${reportId}`,
          payload,
          { headers }
        );
      }
    } else {
      // Create new report
      response = await axios.post(
        "https://backend.schnee.report/content-manager/collection-types/api::report.report" +
          (publish ? "/actions/publish?" : ""),
        payload,
        { headers }
      );
    }

    return {
      success: true,
      documentId: response.data.data.documentId,
      resp: response.data.data,
      hadToUpload: hadToUpload,
    };
  } catch (error: any) {
    console.error("Error saving report:", error);
    return {
      success: false,
      error:
        error.response?.data?.error?.message ||
        "Fehler beim Speichern des Reports",
    };
  }
}
